<template>
    <section :class="`password-changer ${showEditor ? 'password-changer--edit' : 'password-changer--link'}`">
        <h4 v-if="showEditor"
            class="password-changer__subtitle">
            Change Password
        </h4>
        <hh-form v-if="showEditor"
                 class="password-changer__form">
            <hh-form-group>
                <hh-password def="oldPassword"
                             ref="model.OldPassword"
                             class="password-changer__form__password"
                             placeholder="Enter your old password"
                             v-model="oldPassword"
                             @change="resetValidation" />
            </hh-form-group>
            <hh-form-group>
                <hh-password def="newPassword"
                             ref="model.NewPassword"
                             class="password-changer__form__password"
                             placeholder="Enter your new password"
                             v-model="newPassword"
                             @change="resetValidation" />
            </hh-form-group>
            <hh-form-group>
                <hh-password def="confirmPassword"
                             ref="model.ConfirmPassword"
                             class="password-changer__form__password"
                             placeholder="Enter your new password again"
                             v-model="confirmPassword"
                             @change="resetValidation" />
            </hh-form-group>
            <hh-btn type="cancel"
                    class="password-changer__form__button password-changer__form__button--cancel"
                    icon-class="fas fa-undo"
                    icon-location="pre"
                    text="Cancel"
                    @click="onCancelClick" />
            <hh-btn type="cta"
                    class="password-changer__form__button password-changer__form__button--change"
                    icon-class="fas fa-key"
                    icon-location="pre"
                    text="Change Password"
                    @click="onChangePasswordClickAsync" />
        </hh-form>
        <a v-else-if="showStartLinkDesktop"
           class="password-changer__start-link password-changer__start-link--desktop"
           @click="onChangePasswordLinkClick">Change Password</a>
        <a v-else-if="showStartLinkMobile"
           class="password-changer__start-link password-changer__start-link--mobile"
           @click="onChangePasswordLinkClick">Change Password</a>
    </section>
</template>

<script>

import ServiceFactory from '@/services/service-factory'

export default {

    props: {
        showStartLinkDesktop: {
            type: Boolean,
            default: true,
        },
        showStartLinkMobile: {
            type: Boolean,
            default: true,
        },
        show: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            allowValidationPwReset: true,
            editMode: false,

            /**
             * @type {?string}
             */
            oldPassword: null,

            /**
             * @type {?string}
             */
            newPassword: null,

            /**
             * @type {?string}
             */
            confirmPassword: null,
        }
    },

    computed: {

        showEditor() {
            return this.editMode || this.show
        },
    },

    methods: {

        /**
         * Changes the password.
         * @returns {Promise<void>}
         * @private
         */
        async _changePasswordAsync() {
            try {
                const service = ServiceFactory.auth()
                const operationState = await service.changePasswordAsync(
                    this.oldPassword, this.newPassword, this.confirmPassword)

                if (!operationState.succeeded) {
                    operationState.throw()
                } else {
                    this.allowValidationPwReset = true
                    this._resetPasswords()
                    this.resetValidation()
                    this.$emit('changed', { message: 'Password changed successfully.' })
                }
            } catch (err) {
                this._resetPasswords()
                const validation = this.$serverValidator.bindResults(err, this.$refs)

                const error = validation.allErrors.length
                    ? err.message.includes('status code 400')
                        ? `Change password failed: ${ validation.allErrors.join(', ') }`
                        : `Change password failed: ${ err.message } [${ validation.allErrors.join(', ') }]`
                    : `Change password failed: ${ err.message }`

                this.$emit('changed', { error })
            }
        },

        /**
         * Gets the input element node from the DOM.
         * @param {string} refId
         * @returns {HTMLInputElement}
         * @private
         */
        _getRefInputNode(refId) {
            // noinspection JSValidateTypes
            return this.$refs[refId].$el.childNodes[0]
        },

        /**
         * Resets the passwords.
         * @private
         */
        _resetPasswords() {
            this.oldPassword = null
            this._getRefInputNode('model.OldPassword').value = ''
            this.newPassword = null
            this._getRefInputNode('model.NewPassword').value = ''
            this.confirmPassword = null
            this._getRefInputNode('model.ConfirmPassword').value = ''
        },

        /**
         * Event Method.
         * @returns {Promise<void>}
         */
        async onChangePasswordClickAsync() {
            this.allowValidationPwReset = false
            await this._changePasswordAsync()
        },

        /**
         * Event Method.
         */
        onCancelClick() {
            this.allowValidationPwReset = false
            this.editMode = false
            this.$emit('cancel')
        },

        /**
         * Event Method.
         */
        onChangePasswordLinkClick() {
            this.allowValidationPwReset = true
            this.editMode = true
        },

        /**
         * Resets the validation tracker and screen.
         */
        resetValidation() {
            if (!this.allowValidationPwReset) return

            this.$serverValidator.clear(this.$refs)
            this.$emit('clear-validation')
        },
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\components\my-account\_password-changer.scss

.password-changer {
    position: relative;

    @include bp-min-small {
        max-width: rem(350px);
    }

    @include element('form') {

        @include element('password') {
            width: 100%;
        }
    }

    @include modifier('edit') {
        border-style: solid;
        border-width: 2px;
        margin: 1rem 0;
        padding: 0.6rem;
    }

    @include element('subtitle') {
        margin: 0;

        @include bp-min-mid {
            position: absolute;
            top: -0.8rem;
            left: 0.6rem;
            padding: 0 0.2rem;
        }
    }

    @include element('start-link') {
        cursor: pointer;
        font-weight: bold;

        @include bp-min-mid {
            position: relative;
            top: -0.75rem;
            left: 0.92rem;
        }

        @include modifier('desktop') {
            display: none;

            @include bp-min-mid {
                display: inline;
            }
        }

        @include modifier('mobile') {

            @include bp-min-mid {
                display: none;
            }
        }
    }
}

</style>
